import React, { useMemo } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faComment,
  faLightbulb,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

import {
  CANNOT_ESTIMATE_PLACEHOLDER,
  MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS,
  MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_PREFIX,
  createDefaultMusculoskeletalSupplementalData,
  getMusculoskeletalSupplementalFieldDefinition,
  normalizeMusculoskeletalSupplementalData,
} from './dbqMusculoskeletalConstants';

const SECTION_STYLE = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '18px',
  boxShadow: '0 16px 48px rgba(15, 23, 42, 0.06)',
};

const CARD_STYLE = {
  border: '1px solid #e5e7eb',
  borderRadius: '14px',
  background: '#f8fafc',
};

const QUESTION_HELP_STYLE = {
  fontSize: '0.84rem',
  color: '#64748b',
  lineHeight: 1.6,
};

const ACTIVE_REVIEW_STYLE = {
  borderColor: '#60a5fa',
  boxShadow: '0 0 0 3px rgba(96, 165, 250, 0.18)',
};

const ACCEPTED_SUGGESTION_STYLE = {
  borderColor: 'rgba(22, 163, 74, 0.25)',
  borderLeft: '4px solid #16a34a',
  background: 'linear-gradient(90deg, rgba(236, 253, 243, 0.85), rgba(255,255,255,0.95))',
};

const questionText = {
  q1: `At the time of the examination, does the Veteran report taking any medication(s), prescription or non-prescription, to treat the disability being evaluated?`,
  q2: `If Yes, does the medication(s) relieve or reduce the severity of the disability symptoms being evaluated?`,
  q3: `Is it possible to evaluate the severity of the Veteran's disability as if not taking medication (i.e. in an unmedicated state), without resorting to mere speculation?`,
  q4: `If Yes, check all the functional losses that apply. For each item checked below, provide the estimated additional functional loss of the disability. If possible, provide this estimate as additional loss of motion of the disability in the Remarks Section.`,
};

const buildResetFunctionalLosses = () =>
  createDefaultMusculoskeletalSupplementalData().functionalLosses;

const ReviewButton = ({
  fieldId,
  fieldLabel,
  reviewCount = 0,
  canOpenReview = false,
  onReviewClick,
}) => {
  const shouldShow = Boolean(onReviewClick) && (canOpenReview || reviewCount > 0);
  if (!shouldShow || !fieldId) return null;

  return (
    <button
      type="button"
      className={`dbq-comment-btn ms-2 ${reviewCount > 0 ? 'has-comments' : ''}`}
      onClick={() => onReviewClick(fieldId, fieldLabel)}
      title={reviewCount > 0 ? `${reviewCount} review item${reviewCount === 1 ? '' : 's'}` : 'Open review'}
    >
      <FontAwesomeIcon icon={faComment} />
      {reviewCount > 0 && <span className="dbq-comment-badge">{reviewCount}</span>}
    </button>
  );
};

const InlineSuggestionMarker = ({ state = null }) => {
  if (!state) return null;

  return (
    <>
      {state.isAiGenerated ? (
        <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--ai ms-2">
          <FontAwesomeIcon icon={faLightbulb} className="me-1" />
          AI
        </span>
      ) : null}
      {state.isAccepted ? (
        <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--accepted ms-2">
          <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
          Accepted
        </span>
      ) : null}
      {state.isLoading ? (
        <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--loading ms-2">
          <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
          AI loading
        </span>
      ) : null}
      {state.isNotice && !state.isLoading ? (
        <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--notice ms-2">
          <FontAwesomeIcon icon={faLightbulb} className="me-1" />
          AI unavailable
        </span>
      ) : null}
    </>
  );
};

const InlineSuggestionActions = ({
  state = null,
  onMissingDetail = null,
  onEvidence = null,
  onGenerationFailure = null,
  onRejectedSuggestions = null,
}) => {
  if (
    (!state?.isAiGenerated || state.isNotice) &&
    !state?.hasGenerationFailure &&
    !state?.hasRejectedSuggestions
  ) return null;

  return (
    <div className="dbq-inline-suggestion-actions">
      {state.hasGenerationFailure ? (
        <button
          type="button"
          className="dbq-inline-suggestion-action-btn dbq-inline-suggestion-action-btn--failure"
          onClick={onGenerationFailure}
          title="View why AI generation failed for this field"
        >
          Generation Failure
        </button>
      ) : null}
      {state.isAiGenerated && state.hasMissingDetail ? (
        <button
          type="button"
          className="dbq-inline-suggestion-action-btn"
          onClick={onMissingDetail}
          title="Complete the missing detail for this suggestion"
        >
          Complete missing detail
        </button>
      ) : null}
      {state.isAiGenerated ? (
        <button
          type="button"
          className="dbq-inline-suggestion-action-btn dbq-inline-suggestion-action-btn--evidence"
          onClick={onEvidence}
          disabled={!state.hasEvidence}
          title={
            state.hasEvidence
              ? 'View page-level evidence for this AI suggestion'
              : 'No page-level evidence was captured for this AI suggestion'
          }
        >
          View Evidence
        </button>
      ) : null}
      {state.hasRejectedSuggestions ? (
        <button
          type="button"
          className="dbq-inline-suggestion-action-btn dbq-inline-suggestion-action-btn--rejected"
          onClick={onRejectedSuggestions}
          title="View rejected suggestions for this field"
        >
          Rejected Suggestions
        </button>
      ) : null}
    </div>
  );
};

const QuestionCard = ({
  number,
  title,
  children,
  helpText = null,
  disabled = false,
  reviewFieldId = null,
  reviewFieldLabel = null,
  reviewCount = 0,
  canOpenReview = false,
  onReviewClick = null,
  isActiveReview = false,
  inlineSuggestionState = null,
  onInlineMissingDetail = null,
  onInlineEvidence = null,
  onInlineGenerationFailure = null,
  onInlineRejectedSuggestions = null,
}) => (
  <div
    className="p-3 p-md-4"
    style={
      isActiveReview
        ? {
            ...CARD_STYLE,
            ...(inlineSuggestionState?.isAccepted ? ACCEPTED_SUGGESTION_STYLE : {}),
            ...ACTIVE_REVIEW_STYLE,
            opacity: disabled ? 0.72 : 1,
          }
        : {
            ...CARD_STYLE,
            ...(inlineSuggestionState?.isAccepted ? ACCEPTED_SUGGESTION_STYLE : {}),
            opacity: disabled ? 0.72 : 1,
          }
    }
    data-field-id={reviewFieldId || undefined}
    aria-disabled={disabled}
  >
    <div className="fw-semibold mb-2 d-flex align-items-center" style={{ color: '#0f172a' }}>
      <span>{number ? `Q${number}: ${title}` : title}</span>
      <ReviewButton
        fieldId={reviewFieldId}
        fieldLabel={reviewFieldLabel || title}
        reviewCount={reviewCount}
        canOpenReview={canOpenReview}
        onReviewClick={onReviewClick}
      />
      <InlineSuggestionMarker
        state={inlineSuggestionState}
      />
    </div>
    <InlineSuggestionActions
      state={inlineSuggestionState}
      onMissingDetail={onInlineMissingDetail}
      onEvidence={onInlineEvidence}
      onGenerationFailure={onInlineGenerationFailure}
      onRejectedSuggestions={onInlineRejectedSuggestions}
    />
    {helpText ? (
      <div className="mb-3" style={QUESTION_HELP_STYLE}>
        {helpText}
      </div>
    ) : null}
    {inlineSuggestionState?.helpText ? (
      <div className="mb-3 dbq-inline-suggestion-help">
        {inlineSuggestionState.helpText}
      </div>
    ) : null}
    {children}
  </div>
);

const BooleanQuestion = ({
  id,
  name,
  value,
  onChange,
  disabled,
  yesHelp = null,
  noHelp = null,
}) => {
  return (
    <div className="d-flex flex-column gap-2">
      <Form.Check
        id={`${id}-yes`}
        type="radio"
        name={name}
        label="Yes"
        checked={value === true}
        onChange={() => onChange(true)}
        disabled={disabled}
      />
      {yesHelp ? <div className="ms-4" style={QUESTION_HELP_STYLE}>{yesHelp}</div> : null}
      <Form.Check
        id={`${id}-no`}
        type="radio"
        name={name}
        label="No"
        checked={value === false}
        onChange={() => onChange(false)}
        disabled={disabled}
      />
      {noHelp ? <div className="ms-4" style={QUESTION_HELP_STYLE}>{noHelp}</div> : null}
    </div>
  );
};

const DbqMusculoskeletalSupplementalSection = ({
  supplementalData,
  onSupplementalChange,
  readOnly = false,
  // Finalized requests freeze the whole supplemental block (signed-but-not-
  // finalized no longer disables anything - 2026-08-04 rework).
  editLocked = false,
  validationErrors = [],
  activeReviewFieldId = null,
  canOpenReview = false,
  getReviewCount = () => 0,
  onReviewClick = null,
  getInlineSuggestionStateForField = null,
  onOpenInlineSuggestionDetail = null,
  onOpenInlineSuggestionEvidence = null,
  onOpenInlineGenerationFailure = null,
  onOpenInlineRejectedSuggestions = null,
}) => {
  const data = useMemo(
    () => normalizeMusculoskeletalSupplementalData(supplementalData),
    [supplementalData],
  );

  const updateData = (updater) => {
    if (readOnly || typeof onSupplementalChange !== 'function') {
      return;
    }

    const next =
      typeof updater === 'function'
        ? updater(data)
        : updater;

    onSupplementalChange(normalizeMusculoskeletalSupplementalData(next));
  };

  const handleTakingMedicationChange = (nextValue) => {
    updateData((current) => ({
      ...current,
      isTakingMedication: nextValue,
      medicationRelievesSymptoms: nextValue ? current.medicationRelievesSymptoms : null,
      canEstimateWithoutMedication: nextValue ? current.canEstimateWithoutMedication : null,
      cannotEstimateExplanation: nextValue ? current.cannotEstimateExplanation : '',
      functionalLosses: nextValue ? current.functionalLosses : buildResetFunctionalLosses(),
    }));
  };

  const handleMedicationReliefChange = (nextValue) => {
    updateData((current) => ({
      ...current,
      medicationRelievesSymptoms: nextValue,
      canEstimateWithoutMedication: nextValue ? current.canEstimateWithoutMedication : null,
      cannotEstimateExplanation: nextValue ? current.cannotEstimateExplanation : '',
      functionalLosses: nextValue ? current.functionalLosses : buildResetFunctionalLosses(),
    }));
  };

  const handleEstimateChange = (nextValue) => {
    updateData((current) => ({
      ...current,
      canEstimateWithoutMedication: nextValue,
      cannotEstimateExplanation: nextValue ? '' : current.cannotEstimateExplanation,
      functionalLosses: nextValue ? current.functionalLosses : buildResetFunctionalLosses(),
    }));
  };

  const handleFunctionalLossToggle = (key, selected) => {
    updateData((current) => ({
      ...current,
      functionalLosses: {
        ...current.functionalLosses,
        [key]:
          key === 'other'
            ? {
                selected,
                otherLabel: selected ? current.functionalLosses.other.otherLabel : '',
                estimatedLoss: selected ? current.functionalLosses.other.estimatedLoss : '',
              }
            : {
                selected,
                estimatedLoss: selected
                  ? current.functionalLosses[key].estimatedLoss
                  : '',
              },
      },
    }));
  };

  const handleFunctionalLossValueChange = (key, patch) => {
    updateData((current) => ({
      ...current,
      functionalLosses: {
        ...current.functionalLosses,
        [key]: {
          ...current.functionalLosses[key],
          ...patch,
        },
      },
    }));
  };

  // Answer-gating (which drives visibility) is computed separately from the
  // finalized lock so a locked state never HIDES an answered long-text box.
  const question3Gated =
    data.isTakingMedication !== true || data.medicationRelievesSymptoms !== true;
  const question2Disabled =
    readOnly || editLocked || data.isTakingMedication !== true;
  const question3Disabled = readOnly || editLocked || question3Gated;
  const showCannotEstimateExplanation =
    !readOnly && !question3Gated && data.canEstimateWithoutMedication === false;
  const functionalLossesDisabled =
    readOnly ||
    data.isTakingMedication !== true ||
    data.medicationRelievesSymptoms !== true ||
    data.canEstimateWithoutMedication !== true;
  const reviewFieldId = (suffix) => `${MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_PREFIX}.${suffix}`;
  const getInlineSuggestionState = (fieldId) => {
    const field = getMusculoskeletalSupplementalFieldDefinition(fieldId);
    if (!field || typeof getInlineSuggestionStateForField !== 'function') return null;
    return getInlineSuggestionStateForField(field, []);
  };
  const openInlineSuggestionDetail = (fieldId) => {
    if (typeof onOpenInlineSuggestionDetail === 'function') {
      onOpenInlineSuggestionDetail(fieldId);
    }
  };
  const openInlineSuggestionEvidence = (fieldId) => {
    if (typeof onOpenInlineSuggestionEvidence === 'function') {
      onOpenInlineSuggestionEvidence(fieldId);
    }
  };
  const openInlineGenerationFailure = (fieldId) => {
    if (typeof onOpenInlineGenerationFailure === 'function') {
      onOpenInlineGenerationFailure(fieldId);
    }
  };
  const openInlineRejectedSuggestions = (fieldId) => {
    if (typeof onOpenInlineRejectedSuggestions === 'function') {
      onOpenInlineRejectedSuggestions(fieldId);
    }
  };
  const isFieldLocked = (fieldId) =>
    Boolean(getInlineSuggestionState(fieldId)?.isLocked);
  const q1FieldId = reviewFieldId('isTakingMedication');
  const q2FieldId = reviewFieldId('medicationRelievesSymptoms');
  const q3FieldId = reviewFieldId('canEstimateWithoutMedication');
  const q3ExplanationFieldId = reviewFieldId('cannotEstimateExplanation');
  const remarksFieldId = reviewFieldId('remarks');

  return (
    <section
      id="dbq-musculoskeletal-supplemental-section"
      className="mb-5 p-4"
      style={SECTION_STYLE}
    >
      <div className="d-flex flex-column gap-1 mb-4">
        <div
          className="fw-semibold text-uppercase"
          style={{ fontSize: '0.76rem', letterSpacing: '0.08em', color: '#64748b' }}
        >
          Supplemental
        </div>
        <h3 className="mb-0" style={{ color: '#0f172a', fontSize: '1.35rem' }}>
          Supplemental Musculoskeletal Questions
        </h3>
        <div style={QUESTION_HELP_STYLE}>
          These answers are stored with the DBQ and rendered into a separate supplemental PDF in
          the submission packet.
        </div>
      </div>

      {validationErrors.length > 0 ? (
        <Alert variant="danger" className="mb-4">
          <div className="fw-semibold mb-2">Complete the supplemental questions before signing.</div>
          <ul className="mb-0 ps-3">
            {validationErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="d-flex flex-column gap-3">
        <QuestionCard
          number="1"
          title={questionText.q1}
          reviewFieldId={q1FieldId}
          reviewFieldLabel="Supplemental Q1: Taking medication"
          reviewCount={getReviewCount(q1FieldId)}
          canOpenReview={canOpenReview}
          onReviewClick={onReviewClick}
          isActiveReview={activeReviewFieldId === q1FieldId}
          inlineSuggestionState={getInlineSuggestionState(q1FieldId)}
          onInlineMissingDetail={() => openInlineSuggestionDetail(q1FieldId)}
          onInlineEvidence={() => openInlineSuggestionEvidence(q1FieldId)}
          onInlineGenerationFailure={() => openInlineGenerationFailure(q1FieldId)}
          onInlineRejectedSuggestions={() => openInlineRejectedSuggestions(q1FieldId)}
        >
          <BooleanQuestion
            id="msk-q1"
            name="msk-q1"
            value={data.isTakingMedication}
            onChange={handleTakingMedicationChange}
            disabled={readOnly || editLocked || isFieldLocked(q1FieldId)}
            noHelp="If No, do not answer the remaining questions."
          />
        </QuestionCard>

        <QuestionCard
          number="2"
          title={questionText.q2}
          disabled={question2Disabled}
          reviewFieldId={q2FieldId}
          reviewFieldLabel="Supplemental Q2: Medication relieves symptoms"
          reviewCount={getReviewCount(q2FieldId)}
          canOpenReview={canOpenReview}
          onReviewClick={onReviewClick}
          isActiveReview={activeReviewFieldId === q2FieldId}
          inlineSuggestionState={getInlineSuggestionState(q2FieldId)}
          onInlineMissingDetail={() => openInlineSuggestionDetail(q2FieldId)}
          onInlineEvidence={() => openInlineSuggestionEvidence(q2FieldId)}
          onInlineGenerationFailure={() => openInlineGenerationFailure(q2FieldId)}
          onInlineRejectedSuggestions={() => openInlineRejectedSuggestions(q2FieldId)}
        >
          <BooleanQuestion
            id="msk-q2"
            name="msk-q2"
            value={data.medicationRelievesSymptoms}
            onChange={handleMedicationReliefChange}
            disabled={question2Disabled || isFieldLocked(q2FieldId)}
            noHelp="If No, do not answer the remaining questions."
          />
        </QuestionCard>

        <QuestionCard
          number="3"
          title={questionText.q3}
          helpText="If No, provide the required explanation below."
          disabled={question3Disabled}
          reviewFieldId={q3FieldId}
          reviewFieldLabel="Supplemental Q3: Can estimate without medication"
          reviewCount={getReviewCount(q3FieldId)}
          canOpenReview={canOpenReview}
          onReviewClick={onReviewClick}
          isActiveReview={activeReviewFieldId === q3FieldId}
          inlineSuggestionState={getInlineSuggestionState(q3FieldId)}
          onInlineMissingDetail={() => openInlineSuggestionDetail(q3FieldId)}
          onInlineEvidence={() => openInlineSuggestionEvidence(q3FieldId)}
          onInlineGenerationFailure={() => openInlineGenerationFailure(q3FieldId)}
          onInlineRejectedSuggestions={() => openInlineRejectedSuggestions(q3FieldId)}
        >
          <BooleanQuestion
            id="msk-q3"
            name="msk-q3"
            value={data.canEstimateWithoutMedication}
            onChange={handleEstimateChange}
            disabled={question3Disabled || isFieldLocked(q3FieldId)}
            noHelp="If No, provide the required explanation below."
          />

          {showCannotEstimateExplanation ? (
            <Form.Group
              className="mt-3"
              controlId="msk-q3-explanation"
              data-field-id={q3ExplanationFieldId}
            >
              <Form.Label className="fw-medium d-flex align-items-center">
                <span>Explanation</span>
                <ReviewButton
                  fieldId={q3ExplanationFieldId}
                  fieldLabel="Supplemental Q3 explanation"
                  reviewCount={getReviewCount(q3ExplanationFieldId)}
                  canOpenReview={canOpenReview}
                  onReviewClick={onReviewClick}
                />
                <InlineSuggestionMarker
                  state={getInlineSuggestionState(q3ExplanationFieldId)}
                />
              </Form.Label>
              <InlineSuggestionActions
                state={getInlineSuggestionState(q3ExplanationFieldId)}
                onMissingDetail={() => openInlineSuggestionDetail(q3ExplanationFieldId)}
                onEvidence={() => openInlineSuggestionEvidence(q3ExplanationFieldId)}
                onGenerationFailure={() => openInlineGenerationFailure(q3ExplanationFieldId)}
              />
              <Form.Control
                as="textarea"
                rows={4}
                value={data.cannotEstimateExplanation}
                onChange={(event) =>
                  updateData((current) => ({
                    ...current,
                    cannotEstimateExplanation: event.target.value,
                  }))
                }
                placeholder={CANNOT_ESTIMATE_PLACEHOLDER}
                disabled={readOnly || isFieldLocked(q3ExplanationFieldId)}
              />
            </Form.Group>
          ) : null}
        </QuestionCard>

        <QuestionCard number="4" title={questionText.q4} disabled={functionalLossesDisabled}>
          <div className="d-flex flex-column gap-3">
            {MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS.map((option) => {
              const value = data.functionalLosses?.[option.key];
              const selected = Boolean(value?.selected);
              const selectedFieldId = reviewFieldId(`functionalLosses.${option.key}.selected`);
              const estimatedLossFieldId = reviewFieldId(
                `functionalLosses.${option.key}.estimatedLoss`,
              );
              const otherLabelFieldId = reviewFieldId(
                `functionalLosses.${option.key}.otherLabel`,
              );
              const selectedInlineState = getInlineSuggestionState(selectedFieldId);
              const estimatedLossInlineState = getInlineSuggestionState(estimatedLossFieldId);
              const otherLabelInlineState = getInlineSuggestionState(otherLabelFieldId);
              const hasAcceptedInlineSuggestion =
                selectedInlineState?.isAccepted ||
                estimatedLossInlineState?.isAccepted ||
                otherLabelInlineState?.isAccepted;

              return (
                <div
                  key={option.key}
                  className="rounded-3 p-3"
                  style={{
                    background: selected ? '#ffffff' : 'rgba(255,255,255,0.55)',
                    border: hasAcceptedInlineSuggestion
                      ? '1px solid rgba(22, 163, 74, 0.25)'
                      : '1px solid #dbe4ee',
                    borderLeft: hasAcceptedInlineSuggestion ? '4px solid #16a34a' : undefined,
                  }}
                  data-field-id={selectedFieldId}
                >
                  <div className="d-flex align-items-start">
                    <Form.Check
                      id={`msk-loss-${option.key}`}
                      type="checkbox"
                      label={option.label}
                      checked={selected}
                      onChange={(event) =>
                        handleFunctionalLossToggle(option.key, event.target.checked)
                      }
                      disabled={
                        functionalLossesDisabled ||
                        editLocked ||
                        isFieldLocked(selectedFieldId)
                      }
                    />
                    <ReviewButton
                      fieldId={selectedFieldId}
                      fieldLabel={`Supplemental functional loss: ${option.label}`}
                      reviewCount={getReviewCount(selectedFieldId)}
                      canOpenReview={canOpenReview}
                      onReviewClick={onReviewClick}
                    />
                    <InlineSuggestionMarker
                      state={selectedInlineState}
                    />
                  </div>
                  <InlineSuggestionActions
                    state={selectedInlineState}
                    onMissingDetail={() => openInlineSuggestionDetail(selectedFieldId)}
                    onEvidence={() => openInlineSuggestionEvidence(selectedFieldId)}
                    onGenerationFailure={() => openInlineGenerationFailure(selectedFieldId)}
                  />

                  {option.description ? (
                    <div className="ms-4 mt-2" style={QUESTION_HELP_STYLE}>
                      {option.description}
                    </div>
                  ) : null}

                  {selected ? (
                    <div className="ms-4 mt-3 d-flex flex-column gap-3">
                      {option.key === 'other' ? (
                        <Form.Group
                          controlId={`msk-loss-${option.key}-other-label`}
                          data-field-id={otherLabelFieldId}
                        >
                          <Form.Label className="fw-medium d-flex align-items-center">
                            <span>Other label</span>
                            <ReviewButton
                              fieldId={otherLabelFieldId}
                              fieldLabel="Supplemental functional loss: Other label"
                              reviewCount={getReviewCount(otherLabelFieldId)}
                              canOpenReview={canOpenReview}
                              onReviewClick={onReviewClick}
                            />
                            <InlineSuggestionMarker
                              state={otherLabelInlineState}
                            />
                          </Form.Label>
                          <InlineSuggestionActions
                            state={otherLabelInlineState}
                            onMissingDetail={() => openInlineSuggestionDetail(otherLabelFieldId)}
                            onEvidence={() => openInlineSuggestionEvidence(otherLabelFieldId)}
                            onGenerationFailure={() => openInlineGenerationFailure(otherLabelFieldId)}
                          />
                          <Form.Control
                            type="text"
                            value={value?.otherLabel || ''}
                            onChange={(event) =>
                              handleFunctionalLossValueChange(option.key, {
                                otherLabel: event.target.value,
                              })
                            }
                            placeholder="Describe the other functional loss"
                            disabled={
                              readOnly ||
                              functionalLossesDisabled ||
                              editLocked ||
                              isFieldLocked(otherLabelFieldId)
                            }
                          />
                        </Form.Group>
                      ) : null}

                      <Form.Group
                        controlId={`msk-loss-${option.key}-estimated-loss`}
                        data-field-id={estimatedLossFieldId}
                      >
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <span>Estimated additional functional loss</span>
                          <ReviewButton
                            fieldId={estimatedLossFieldId}
                            fieldLabel={`Supplemental estimated loss: ${option.label}`}
                            reviewCount={getReviewCount(estimatedLossFieldId)}
                            canOpenReview={canOpenReview}
                            onReviewClick={onReviewClick}
                          />
                          <InlineSuggestionMarker
                            state={estimatedLossInlineState}
                          />
                        </Form.Label>
                        <InlineSuggestionActions
                          state={estimatedLossInlineState}
                          onMissingDetail={() => openInlineSuggestionDetail(estimatedLossFieldId)}
                          onEvidence={() => openInlineSuggestionEvidence(estimatedLossFieldId)}
                          onGenerationFailure={() => openInlineGenerationFailure(estimatedLossFieldId)}
                        />
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={value?.estimatedLoss || ''}
                          onChange={(event) =>
                            handleFunctionalLossValueChange(option.key, {
                              estimatedLoss: event.target.value,
                            })
                          }
                          placeholder="Provide the estimated additional functional loss for this item."
                          disabled={
                            readOnly ||
                            functionalLossesDisabled ||
                            isFieldLocked(estimatedLossFieldId)
                          }
                        />
                      </Form.Group>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </QuestionCard>

        <QuestionCard
          title="Supplemental remarks"
          helpText="Use this space for any additional supplemental-specific remarks. This field is not limited by the DBQ PDF template."
          reviewFieldId={remarksFieldId}
          reviewFieldLabel="Supplemental remarks"
          reviewCount={getReviewCount(remarksFieldId)}
          canOpenReview={canOpenReview}
          onReviewClick={onReviewClick}
          isActiveReview={activeReviewFieldId === remarksFieldId}
          inlineSuggestionState={getInlineSuggestionState(remarksFieldId)}
          onInlineMissingDetail={() => openInlineSuggestionDetail(remarksFieldId)}
          onInlineEvidence={() => openInlineSuggestionEvidence(remarksFieldId)}
          onInlineGenerationFailure={() => openInlineGenerationFailure(remarksFieldId)}
          onInlineRejectedSuggestions={() => openInlineRejectedSuggestions(remarksFieldId)}
        >
          <Form.Group
            className="mb-0"
            controlId="msk-supplemental-remarks"
            data-field-id={remarksFieldId}
          >
            <Form.Control
              as="textarea"
              rows={5}
              value={data.remarks}
              onChange={(event) =>
                updateData((current) => ({
                  ...current,
                  remarks: event.target.value,
                }))
              }
              placeholder="Add supplemental remarks"
              disabled={readOnly || isFieldLocked(remarksFieldId)}
            />
          </Form.Group>
        </QuestionCard>
      </div>
    </section>
  );
};

export default DbqMusculoskeletalSupplementalSection;
